import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Reveal from "@/components/common/Reveal";

const faqs = [
  ["What exactly does DIBRAL do?", "DIBRAL designs, builds and runs custom AI automation systems for small and medium businesses — websites, WhatsApp assistants, CRMs, lead management, support, dashboards and more."],
  ["Who is DIBRAL for?", "Ambitious SMBs in real estate, healthcare, clinics, education, automobile, restaurants, retail and local services that want to scale operations without scaling headcount."],
  ["How fast can you launch?", "Most systems go live in 3–14 days, depending on scope. We start with a focused MVP that already produces ROI, then expand."],
  ["Do you build websites or only automation?", "Both. We build premium AI-powered websites and connect them to your automation stack so every visitor turns into pipeline."],
  ["Is WhatsApp automation officially allowed?", "Yes. We use the official WhatsApp Business API, which is fully compliant and allows AI-driven conversations, broadcasts and order flows."],
  ["Will the AI sound like my brand?", "Absolutely. We train every assistant on your tone, FAQs and product knowledge so it feels native to your business."],
  ["How do you integrate with my existing tools?", "We integrate with most CRMs, calendars, payment gateways and helpdesks via APIs and webhooks — no rip-and-replace required."],
  ["What about my data — is it secure?", "All data is encrypted in transit and at rest. We follow role-based access control and can deploy in your private environment on request."],
  ["What if my team isn't tech-savvy?", "That's the whole point of DIBRAL. Everything runs in the background — your team gets clean dashboards and human-readable notifications."],
  ["Do you offer ongoing support?", "Yes, every engagement includes continuous monitoring, optimisation and a monthly business review."],
  ["What does a typical engagement cost?", "Projects start from a one-time setup fee plus a monthly retainer. We share clear pricing on the discovery call after understanding scope."],
  ["Do you offer a money-back guarantee?", "We offer clear milestones and KPIs. If the first milestone isn't met, you don't pay for the next one. We win when you win."],
  ["Can you work with very small businesses?", "Yes. We have lightweight packages designed for solo founders and 2–10 person teams to get their first AI system live."],
  ["Will AI replace my team?", "No. AI replaces the boring, repetitive 70%. Your team focuses on relationships, deals and creativity — the parts only humans can do."],
  ["How do you measure ROI?", "We agree on 2–3 KPIs upfront (e.g. booked meetings, response time, conversion rate) and track them in your live dashboard."],
  ["Do you build only in English?", "No — our WhatsApp assistants speak Hindi, English, regional Indian languages and more. Multilingual is the default, not an add-on."],
  ["What industries do you NOT serve?", "We avoid sectors that conflict with our ethics policy (e.g. predatory loans, fake reviews). Everything else is fair game."],
  ["Can you help with marketing, not just automation?", "Yes. We design conversion-focused funnels, lead magnets and re-engagement flows — automation only matters if leads are flowing in."],
  ["How do I know if my business is ready for AI?", "If you have leads coming in, a sales process and repetitive tasks — you're ready. Book a free consultation and we'll tell you honestly."],
  ["What happens after the free consultation?", "If there's a fit, we send a tailored proposal within 48 hours. If there isn't, you still walk away with a clear AI roadmap — free of cost."],
];

export default function Faq() {
  return (
    <section id="faq" className="section relative" data-testid="faq-section">
      <div className="container-x relative">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Reveal direction="up">
              <span className="eyebrow">
                <span className="dot" /> FAQ
              </span>
            </Reveal>
            <Reveal direction="up" delay={0.05}>
              <h2 className="h-display mt-5 text-4xl sm:text-5xl text-[var(--body-fg)]">
                Common questions.<br />
                <span className="font-serif italic">Honest answers.</span>
              </h2>
            </Reveal>
            <Reveal direction="up" delay={0.1}>
              <p className="mt-5 text-[var(--body-fg)]/60">
                Don't see your question? Ping us on WhatsApp — we usually reply
                within a few minutes (it's our own AI handling it).
              </p>
            </Reveal>
          </div>

          <Reveal
            direction="up"
            delay={0.12}
            stagger={0.04}
            childrenSelector="[data-faq-item]"
            className="lg:col-span-8"
          >
            <Accordion type="single" collapsible className="space-y-3">
              {faqs.map(([q, a], i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  data-testid={`faq-item-${i}`}
                  data-faq-item
                  className="border-0 glass glow-border rounded-2xl px-5 overflow-hidden tilt-card"
                >
                  <AccordionTrigger className="text-left text-[var(--body-fg)] hover:no-underline py-5 text-base hover:text-[var(--brand)] transition-colors">
                    {q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[var(--body-fg)]/65 leading-relaxed pb-5">
                    {a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
